import React, { useRef } from 'react';
import type { FC, ReactNode } from 'react';
import { IconButton, fade, makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircleRounded';
import ErrorIcon from '@material-ui/icons/ErrorRounded';
import WarningIcon from '@material-ui/icons/WarningRounded';
import InfoIcon from '@material-ui/icons/InfoRounded';
import CloseIcon from '@material-ui/icons/Close';
import { SnackbarProvider } from 'notistack';
import { useTranslation } from 'react-i18next';
import type { Theme } from 'src/theme';

// App-wide toasts: a theme-coloured card with a coloured accent strip and icon,
// instead of notistack's solid green/red blocks. Bottom-right so they don't
// cover the sidebar. Every existing enqueueSnackbar() call gets this look.
const useStyles = makeStyles((theme: Theme) => {
  const variant = (color: string) => ({
    // Two classes so this beats notistack's own variant background.
    '&.MuiSnackbarContent-root': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: `inset 3px 0 0 ${color}, ${theme.shadows[8]}`,
      '& $icon': { color }
    }
  });
  return {
    base: {
      '&.MuiSnackbarContent-root': {
        minWidth: 320,
        maxWidth: 440,
        padding: theme.spacing(0.75, 1, 0.75, 2),
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        fontFamily: theme.typography.fontFamily,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.4
      },
      '& .MuiSnackbarContent-message': {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.75, 0)
      },
      '& .MuiSnackbarContent-action': {
        marginRight: 0,
        paddingLeft: theme.spacing(1)
      }
    },
    success: variant(theme.palette.success.main),
    error: variant(theme.palette.error.main),
    warning: variant(theme.palette.warning.main),
    info: variant(theme.palette.info.main),
    icon: {
      fontSize: 20,
      marginRight: theme.spacing(1.25),
      flex: 'none'
    },
    close: {
      color: theme.palette.text.secondary,
      '&:hover': { backgroundColor: fade(theme.palette.text.primary, 0.08) }
    }
  };
});

const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const ref = useRef<any>(null);

  return (
    <SnackbarProvider
      ref={ref}
      maxSnack={3}
      dense
      preventDuplicate
      autoHideDuration={4000}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      classes={{
        variantSuccess: `${classes.base} ${classes.success}`,
        variantError: `${classes.base} ${classes.error}`,
        variantWarning: `${classes.base} ${classes.warning}`,
        variantInfo: `${classes.base} ${classes.info}`
      }}
      iconVariant={{
        success: <CheckCircleIcon className={classes.icon} />,
        error: <ErrorIcon className={classes.icon} />,
        warning: <WarningIcon className={classes.icon} />,
        info: <InfoIcon className={classes.icon} />
      }}
      action={key => (
        <IconButton size="small" className={classes.close} aria-label={t('Dismiss')} onClick={() => ref.current?.closeSnackbar(key)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    >
      {children}
    </SnackbarProvider>
  );
};

export default ToastProvider;
